'use client';

/**
 * Team Plan signup / flow header implementation (not Stencil-generated).
 *
 * App routes should import {@link Header} from `@/components/header/team-plan`, not from
 * `@/components/header`, so `stencil upgrade` can reset `index.tsx` / `default-header.tsx` without
 * affecting this micro frontend.
 */

import type {CSSProperties} from 'react';

import {useRouter} from 'next/navigation';

import {useI18n} from '@udemy-v2/i18n';
import {getUdemyLogos} from '@udemy-v2/react-brand-components';
import {Button, Image} from '@udemy-v2/react-core-components';
import {useSmartBarRef} from '@udemy-v2/react-smart-bar';
import {MinimalHeader} from '@udemy-v2/react-structure-components';

import styles from './team-plan-header.module.scss';
import {LanguageSelectorButton, SelectorLocation} from '@udemy-v2/react-language-selector';
import {UB_HOME_URL} from '@/app/[lang]/constants';

/**
 * Whether close should call `router.back()` instead of going to the UB home.
 *
 * Uses the Navigation API's {@link https://developer.mozilla.org/en-US/docs/Web/API/Navigation/canGoBack | canGoBack}
 * when present. We intentionally do not use `window.history.length` (unreliable for new tabs) or
 * `document.referrer` (omitted for privacy / cross-origin).
 */
function shouldUseBackNavigation(): boolean {
    const nav = (globalThis as {navigation?: {canGoBack?: boolean}}).navigation;
    return nav?.canGoBack ?? false;
}

/**
 * Props for the team-plan {@link Header} (fixed MinimalHeader + layout shell).
 */
export interface HeaderProps {
    /**
     * When true (default), `top` and the shell spacer include {@link useSmartBarRef}'s
     * pixel offset while the Smart Bar is sticky so the bar is not covered.
     */
    includeSmartBarOffset?: boolean;
    /** Extra pixels added to fixed `top` and the shell min height (banners, experiments). */
    extraTopOffsetPx?: number;
}

export function Header({includeSmartBarOffset = true, extraTopOffsetPx = 0}: HeaderProps) {
    const router = useRouter();
    const {gettext} = useI18n();
    const {offset: smartBarOffsetPx} = useSmartBarRef();
    const topOffsetPx =
        (includeSmartBarOffset ? smartBarOffsetPx : 0) + Math.max(0, extraTopOffsetPx);

    const ub = getUdemyLogos();
    const image = <Image src={ub.ubLogoUrl} width={150} height={100} alt={'ub'} />;
    const renderLogo = () => <a href={UB_HOME_URL}>{image}</a>;

    const onCloseClick = () =>
        shouldUseBackNavigation() ? router.back() : window.location.assign(UB_HOME_URL);

    return (
        <div
            className={styles.shell}
            style={
                {
                    '--header-top-offset': `${topOffsetPx}px`,
                } as CSSProperties
            }
        >
            <MinimalHeader className={styles.header} renderLogo={renderLogo}>
                <div className={styles.actions}>
                    <Button size="large" udStyle="ghost" type="button" onClick={onCloseClick}>
                        {gettext('Close')}
                    </Button>
                    <LanguageSelectorButton
                        withLabel={false}
                        selectorLocation={SelectorLocation.DesktopHeader}
                        onLocaleChange={() => window.location.reload()}
                    />
                </div>
            </MinimalHeader>
        </div>
    );
}
